<template>
  <div class="common-programme-status" :class="cssClassColor"
       v-if="runConcentration && runCourses && runCourses.length > 0">
    <div class="common-programme-status__headline">
      {{ runConcentration.title }}
    </div>

    <div class="common-programme-status__progress" v-if="progress">
      <div class="common-programme-status__progress-bar">
        <div class="common-programme-status__progress-fill" :style="progressStyle"/>
      </div>

      <div class="common-programme-status__progress-number">
        {{ localizedPercent }}
      </div>
    </div>

    <template v-if="runCourses">
      <ProfileCourseStatus
        v-for="runCourse in runCourses"
        :key="`run-course-${runCourse.id}`"
        :run-course="runCourse"
        :programme-code="runConcentration.code"/>
    </template>
  </div>
</template>

<script setup>
  const props = defineProps({
    runConcentration: Object,
    onlyActiveCourse: Boolean,
  });

  const route = useRoute();

  const formattedDate = (dateString) => {
    const date = new Date(dateString);
    const lang = route.params.locale === 'en' ? 'en-GB' : route.params.locale;
    return date.toLocaleDateString(lang);
  };

  const cssClassColor = computed(() => `common-programme-status--${props.runConcentration.programmeType}`);

  const runCourses = computed(() => {
    if (props.onlyActiveCourse) {
      return props.runConcentration.courses.filter((course) => course.status !== 'finished' && course.studentStatusInCourse === 'active');
    }
    return props.runConcentration.courses.filter((course) => course.status !== 'finished');
  });

  const progress = computed(() => {
    let { startDate, endDate } = props.runConcentration;
    if (!startDate && runCourses.value) {
      startDate = runCourses.value[0].startDate;
    }
    if (!endDate && runCourses.value) {
      endDate = runCourses.value[0].endDate;
    }

    if (!startDate || !endDate) {
      return 0;
    }

    const startDateDate = new Date(startDate);
    const endDateDate = new Date(endDate);
    const now = new Date();
    const percent = ((now.getTime() - startDateDate.getTime()) / (endDateDate.getTime() - startDateDate.getTime())) * 100;
    return Math.min(Math.max(0, percent), 100);
  });

  const progressStyle = computed(() => `width: ${progress.value ?? 0}%`);

  const localizedNumber = computed(() => progress.value.toLocaleString('en', { style: 'decimal' }));

  const localizedPercent = computed(() => {
    const percent = progress.value / 100;

    return percent.toLocaleString('en', { style: 'percent' });
  });
</script>

<style lang="scss">
@use "../../styles/mixins";
@use "../../styles/variables";

.common-programme-status {
  @include mixins.modifierVars('--course-status');

  align-items: stretch;
  border: 2px solid var(--course-status-color);
  border-radius: 1px;
  color: var(--course-status-color);
  display: flex;
  flex-flow: column;
  justify-content: flex-start;
  margin-bottom: .5rem;
  padding: 1.5rem 2rem;

  @include mixins.mq(variables.$mq-phone) {
    padding: .75rem 1rem;
  }

  &__box {
    align-items: flex-start;
    background-color: var(--course-status-color);
    border-radius: var(--border-radius);
    box-sizing: border-box;
    color: var(--color-white);
    display: flex;
    flex-flow: column;
    justify-content: flex-start;
    padding: 1rem 1.25rem;
    width: 100%;

    &:not(:last-child) {
      margin-bottom: 1rem;
    }
  }

  &__headline {
    font-size: 1.25rem;
    margin-bottom: .5rem;
    text-transform: uppercase;
  }

  &__box-headline {
    display: flex;
    font-size: 1.3rem;
    margin-bottom: .5rem;

    @include mixins.mq(variables.$mq-phone) {
      font-size: 1rem;
    }
  }

  &__subheadline {
    display: flex;
    font-weight: variables.$font-weight-light;
    margin-bottom: .5rem;

    @include mixins.mq(variables.$mq-phone) {
      font-size: .9rem;
    }
  }

  &__classroom {
    font-weight: variables.$font-weight-light;

    @include mixins.mq(variables.$mq-phone) {
      font-size: .9rem;
    }
  }

  &__progress {
    align-items: center;
    display: flex;
    flex-flow: row nowrap;
    margin-bottom: 1rem;
    margin-top: auto;
    width: 100%;
  }

  &__progress-bar {
    @include mixins.ltrtl(margin-right, margin-left, 1rem);

    align-items: stretch;
    background-color: var(--color-gray-200);
    border-radius: .175rem;
    display: flex;
    height: .35rem;
    justify-content: flex-start;
    overflow: hidden;
    width: 100%;
  }

  &__progress-fill {
    background-color: currentColor;
    border-radius: .175rem;
    height: 100%;
  }

  &__unit {
    align-items: center;
    border-top: 1px solid var(--color-white);
    display: flex;
    justify-content: space-between;
    margin-top: .75rem;
    padding-top: .75rem;
    width: 100%;

    @include mixins.mq(variables.$mq-phone) {
      align-items: stretch;
      flex-flow: column;
      width: 100%;

      span {
        display: inline-block;
        margin-bottom: .75rem;
      }
    }
  }

  &__unit-button {
    @include mixins.ltrtl(margin-left, margin-right, auto);

    background-color: var(--course-status-color);
    border: .1rem solid var(--color-white);
    border-radius: 1.25rem;
    box-sizing: border-box;
    color: var(--color-white);
    padding: .25rem 1.25rem;
    text-decoration: none;
    transition: background-color .25s, color .25s;

    &:hover {
      background-color: var(--color-white);
      color: var(--course-status-color);
    }

    @include mixins.mq(variables.$mq-phone) {
      @include mixins.ltrtl(margin-left, margin-right, 0);

      text-align: center;
      width: 100%;
    }
  }
}
</style>
